/* The Mint Workshop — interactive behaviours.
   Flip cards, before/after compare slider, work log filtering, parallax.
   Everything degrades to a usable static state without JS. */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------------------
     Hero: floating flip cards
     ---------------------------------------------------------------------- */
  function initBench(bench) {
    if (bench.dataset.mwReady) return;
    bench.dataset.mwReady = '1';
    bench.querySelectorAll('.mw-bench__slab').forEach(function (slab) {
      slab.addEventListener('click', function () {
        var f = slab.classList.toggle('is-flipped');
        slab.setAttribute('aria-pressed', f ? 'true' : 'false');
      });
    });
  }

  function initStage(stage) {
    if (stage.dataset.mwReady) return;
    stage.dataset.mwReady = '1';

    var cards = [].slice.call(stage.querySelectorAll('.mw-fc'));

    cards.forEach(function (card) {
      // Flip on click / Enter / Space. The element is a <button>, so keyboard
      // activation already fires click.
      card.addEventListener('click', function () {
        var flipped = card.classList.toggle('is-flipped');
        card.setAttribute('aria-pressed', flipped ? 'true' : 'false');
        stage.classList.add('is-touched');
      });

      // Foil sheen follows the pointer across the face
      card.addEventListener('pointermove', function (e) {
        var r = card.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width;
        var y = (e.clientY - r.top) / r.height;
        var angle = 90 + (x - 0.5) * 90 + (y - 0.5) * 30;
        card.style.setProperty('--sheen-angle', angle.toFixed(1) + 'deg');
        card.style.setProperty('--sheen', '1');
      });
      card.addEventListener('pointerleave', function () {
        card.style.setProperty('--sheen', '0');
      });
    });

    if (reduced) return;

    // Parallax: cards drift against the pointer, depth scaled by card size
    var raf = null, tx = 0, ty = 0;

    function apply() {
      raf = null;
      cards.forEach(function (card) {
        var depth = parseFloat(card.dataset.depth || '1');
        card.style.setProperty('--px', (tx * 26 * depth).toFixed(1) + 'px');
        card.style.setProperty('--py', (ty * 18 * depth).toFixed(1) + 'px');
      });
    }

    stage.addEventListener('pointermove', function (e) {
      var r = stage.getBoundingClientRect();
      tx = (e.clientX - r.left) / r.width - 0.5;
      ty = (e.clientY - r.top) / r.height - 0.5;
      if (!raf) raf = requestAnimationFrame(apply);
    });

    stage.addEventListener('pointerleave', function () {
      tx = 0; ty = 0;
      if (!raf) raf = requestAnimationFrame(apply);
    });
  }

  /* ----------------------------------------------------------------------
     Before / after compare slider
     ---------------------------------------------------------------------- */
  function initCompare(el) {
    if (el.dataset.mwReady) return;
    el.dataset.mwReady = '1';

    var dragging = false;

    function set(clientX) {
      var r = el.getBoundingClientRect();
      var pct = ((clientX - r.left) / r.width) * 100;
      pct = Math.max(0, Math.min(100, pct));
      el.style.setProperty('--pos', pct.toFixed(2) + '%');
      el.setAttribute('aria-valuenow', Math.round(pct));
    }

    el.addEventListener('pointerdown', function (e) {
      dragging = true;
      el.setPointerCapture(e.pointerId);
      set(e.clientX);
    });
    el.addEventListener('pointermove', function (e) {
      if (dragging) set(e.clientX);
    });
    ['pointerup', 'pointercancel'].forEach(function (ev) {
      el.addEventListener(ev, function () { dragging = false; });
    });

    // Keyboard: arrows nudge, Home/End jump
    el.addEventListener('keydown', function (e) {
      var cur = parseFloat(el.style.getPropertyValue('--pos')) || 50;
      var next = cur;
      if (e.key === 'ArrowLeft') next = cur - 4;
      else if (e.key === 'ArrowRight') next = cur + 4;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = 100;
      else return;
      e.preventDefault();
      next = Math.max(0, Math.min(100, next));
      el.style.setProperty('--pos', next + '%');
      el.setAttribute('aria-valuenow', Math.round(next));
    });
  }

  /* ----------------------------------------------------------------------
     Work log: filtering, tap-to-toggle, show more
     ---------------------------------------------------------------------- */
  function initLog(log) {
    if (log.dataset.mwReady) return;
    log.dataset.mwReady = '1';

    var grid = log.querySelector('.mw-log__grid');
    var filters = [].slice.call(log.querySelectorAll('.mw-log__filter'));
    var countEl = log.querySelector('[data-log-count]');
    var moreBtn = log.querySelector('[data-log-more]');
    var step = parseInt(log.dataset.pageSize || '12', 10);
    var shown = step;
    var active = 'all';

    // Hand-picked cards (section blocks) always come first.
    var fixed = [].slice.call(log.querySelectorAll('.mw-log__item:not([data-arch])'));

    // Intake archive: only the first page is in the HTML. The full list lives in a
    // cached data file and is fetched when someone filters or asks for more.
    var archEl = log.querySelector('[data-mw-archive]');
    var arch = null;
    if (archEl) {
      arch = {
        src: archEl.dataset.src,
        base: archEl.dataset.base,
        total: parseInt(archEl.dataset.total || '0', 10),
        counts: {},
        slugs: {},
        els: [],        // DOM node per archive index, created on demand
        data: null,
        loading: null
      };
      (archEl.dataset.slugs || '').split(',').forEach(function (kv) {
        var i = kv.indexOf(':');
        if (i > 0) arch.slugs[kv.slice(0, i)] = kv.slice(i + 1);
      });
      (archEl.dataset.counts || '').split(',').forEach(function (kv) {
        var i = kv.indexOf(':');
        if (i > 0) arch.counts[arch.slugs[kv.slice(0, i)] || kv.slice(0, i)] = parseInt(kv.slice(i + 1), 10) || 0;
      });
      [].slice.call(log.querySelectorAll('.mw-log__item[data-arch]')).forEach(function (el) {
        arch.els[parseInt(el.dataset.arch, 10)] = el;
      });
    }

    var empty = document.createElement('p');
    empty.className = 'mw-log__empty';
    empty.textContent = 'No cards in this category yet.';
    empty.hidden = true;
    if (grid) grid.appendChild(empty);

    function loadArchive() {
      if (!arch) return Promise.resolve();
      if (arch.data) return Promise.resolve();
      if (window.MW_ARCHIVE) { arch.data = window.MW_ARCHIVE; return Promise.resolve(); }
      if (!arch.loading) {
        arch.loading = new Promise(function (resolve) {
          var sc = document.createElement('script');
          sc.src = arch.src;
          sc.async = true;
          sc.onload = function () { arch.data = window.MW_ARCHIVE || []; resolve(); };
          sc.onerror = function () { arch.loading = null; resolve(); };
          document.head.appendChild(sc);
        });
      }
      return arch.loading;
    }

    // Fetch quietly once the work log is getting close, so taps feel instant.
    if (arch && 'IntersectionObserver' in window) {
      var pre = new IntersectionObserver(function (en) {
        if (en.some(function (e) { return e.isIntersecting; })) { pre.disconnect(); loadArchive(); }
      }, { rootMargin: '800px 0px' });
      pre.observe(log);
    }

    function esc(t) {
      return String(t).replace(/[&<>"]/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
      });
    }

    var catNames = { p: 'Pokémon', s: 'Sports', o: 'One Piece', m: 'Magic: The Gathering', x: 'Other TCG' };

    function makeItem(i) {
      var e = arch.data[i];
      var src = arch.base + e[1];
      var name = e[2], line = e[3];
      var alt = name ? (line ? name + ', ' + line : name) : 'Customer ' + (catNames[e[0]] || 'trading') + ' card, scanned at intake';
      var el = document.createElement('div');
      el.className = 'mw-log__item mw-log__item--late';
      el.dataset.cat = arch.slugs[e[0]] || '';
      el.dataset.arch = String(i);
      el.innerHTML =
        '<button type="button" class="mw-log__card" aria-label="' + esc(alt) + '">' +
          '<span class="mw-log__face"><img src="' + src + '?width=500" srcset="' + src + '?width=300 300w, ' + src +
          '?width=500 500w" sizes="(min-width: 990px) 25vw, 50vw" alt="' + esc(alt) +
          '" loading="lazy" decoding="async" width="500" height="700"></span>' +
        '</button>' +
        (name ? '<div class="mw-log__meta"><p class="mw-log__name">' + esc(name) + '</p>' +
          (line ? '<p class="mw-log__svc">' + esc(line) + '</p>' : '') + '</div>' : '');
      el.querySelector('.mw-log__card').addEventListener('click', toggleBefore);
      // keep grid order: insert before the next archive card that already exists
      var next = empty;
      for (var j = i + 1; j < arch.els.length; j++) {
        if (arch.els[j]) { next = arch.els[j]; break; }
      }
      grid.insertBefore(el, next);
      arch.els[i] = el;
      return el;
    }

    function matchesCat(cat) {
      if (active === 'all') return true;
      return (cat || '').split(',').indexOf(active) > -1;
    }

    function render() {
      var visible = 0, total = 0;

      fixed.forEach(function (item) {
        if (!matchesCat(item.dataset.cat)) { item.classList.add('is-out'); return; }
        total++;
        if (visible < shown) { item.classList.remove('is-out'); visible++; }
        else item.classList.add('is-out');
      });

      if (arch) {
        if (arch.data) {
          for (var i = 0; i < arch.data.length; i++) {
            var slug = arch.slugs[arch.data[i][0]];
            var el = arch.els[i];
            if (!matchesCat(slug)) { if (el) el.classList.add('is-out'); continue; }
            total++;
            if (visible < shown) {
              if (!el) el = makeItem(i);
              el.classList.remove('is-out');
              visible++;
            } else if (el) {
              el.classList.add('is-out');
            }
          }
        } else {
          // Not loaded yet: work with the cards already in the page, count from the summary.
          arch.els.forEach(function (el) {
            if (!el) return;
            if (!matchesCat(el.dataset.cat)) { el.classList.add('is-out'); return; }
            if (visible < shown) { el.classList.remove('is-out'); visible++; }
            else el.classList.add('is-out');
          });
          total += active === 'all' ? arch.total : (arch.counts[active] || 0);
        }
      }

      if (countEl) countEl.innerHTML = '<b>' + total.toLocaleString() + '</b> ' + (total === 1 ? 'card' : 'cards');
      empty.hidden = total !== 0;
      if (moreBtn) moreBtn.parentElement.style.display = total > visible ? '' : 'none';
    }

    // Render now if the cards on hand fill the page; otherwise fetch first.
    function update() {
      var needData = arch && !arch.data;
      if (!needData) { render(); return; }
      render();
      log.classList.add('is-loading');
      loadArchive().then(function () {
        log.classList.remove('is-loading');
        render();
      });
    }

    filters.forEach(function (btn) {
      btn.addEventListener('click', function () {
        active = btn.dataset.filter;
        shown = step;
        filters.forEach(function (b) {
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
        update();
      });
    });

    if (moreBtn) {
      moreBtn.addEventListener('click', function () {
        shown += step;
        update();
      });
    }

    // On touch devices hover cannot expose the "before" image, so tap toggles it
    function toggleBefore() { this.classList.toggle('is-showing-before'); }
    log.querySelectorAll('.mw-log__card').forEach(function (card) {
      card.addEventListener('click', toggleBefore);
    });

    render();
  }

  /* ----------------------------------------------------------------------
     Scroll-linked parallax on images
     ---------------------------------------------------------------------- */
  function initParallax() {
    if (reduced) return;
    var els = [].slice.call(document.querySelectorAll('.mw-parallax'));
    if (!els.length) return;
    var ticking = false;

    function update() {
      ticking = false;
      var vh = window.innerHeight;
      els.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        var progress = (r.top + r.height / 2 - vh / 2) / vh;
        el.style.setProperty('--shift', (progress * -34).toFixed(1) + 'px');
      });
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */
  function init() {
    document.querySelectorAll('.mw-stage').forEach(initStage);
    document.querySelectorAll('.mw-bench__stage').forEach(initBench);
    document.querySelectorAll('.mw-cmp').forEach(initCompare);
    document.querySelectorAll('[data-mw-log]').forEach(initLog);
    initParallax();

    // Masked image reveals share the reveal observer pattern
    if (!reduced && 'IntersectionObserver' in window) {
      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { en.target.classList.add('is-in'); obs.unobserve(en.target); }
        });
      }, { threshold: 0.2 });
      document.querySelectorAll('.mw-mask').forEach(function (el) {
        if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-in');
        else obs.observe(el);
      });
    } else {
      document.querySelectorAll('.mw-mask').forEach(function (el) { el.classList.add('is-in'); });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () { setTimeout(init, 40); });
})();
